import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../api/httpClient";
import InfoCard from "../../components/card/infocard";
import {
  DeleteItem,
  InviteModal,
  SuccessfulDelete,
  SuccessModal,
} from "../../components/modal";
import DeleteImg from "../../assets/delLogo.png";
import verifyImg from "../../assets/verifyImg.png";
import { SimpleButton } from "../../components/button.jsx/Btn";
import { getApiBaseUrl } from "../../data/authStorage";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";

const LeaveFlowDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const apiBaseUrl = getApiBaseUrl();

  const approvalFlowId = useMemo(
    () => location.state?.approvalFlowId || sessionStorage.getItem("selectedApprovalFlowId"),
    [location.state],
  );
  const stepId = useMemo(
    () => location.state?.stepId || sessionStorage.getItem("selectedApprovalFlowStepId"),
    [location.state],
  );
  const cacheKey = useMemo(
    () => `cehr_leave_flow_step_${approvalFlowId || "unknown"}_${stepId || "unknown"}`,
    [approvalFlowId, stepId],
  );
  const cachedData = useMemo(() => readSessionCache(cacheKey, { step: null }), [cacheKey]);

  const [step, setStep] = useState(cachedData?.step || null);
  const [levels, setLevels] = useState([]);
  const [positions, setPositions] = useState([]);
  const [levelId, setLevelId] = useState("");
  const [selectedPositionIds, setSelectedPositionIds] = useState([]);
  const [draggedPositionId, setDraggedPositionId] = useState("");
  const [isLoading, setIsLoading] = useState(!cachedData?.step);
  const [errorMessage, setErrorMessage] = useState("");
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openDeleteSuccessModal, setOpenDeleteSuccessModal] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadLookups = useCallback(async () => {
    if (!apiBaseUrl) return;

    const [levelsResponse, positionsResponse] = await Promise.all([
      axios.get(`${apiBaseUrl}/organizations/levels`),
      axios.get(`${apiBaseUrl}/organizations/positions`),
    ]);

    setLevels(levelsResponse?.data?.levels || []);
    setPositions(positionsResponse?.data?.positions || []);
  }, [apiBaseUrl]);

  const loadStep = useCallback(async ({ silent = false } = {}) => {
    if (!apiBaseUrl || !approvalFlowId || !stepId) {
      setErrorMessage("Select an approval flow step first.");
      return;
    }

    try {
      if (!silent) {
        setIsLoading(true);
      }
      setErrorMessage("");

      const response = await axios.get(
        `${apiBaseUrl}/organizations/approval-flows/${approvalFlowId}/steps/${stepId}`,
      );
      const loadedStep = response?.data?.step || null;
      setStep(loadedStep);
      setLevelId(loadedStep?.level?.id || "");
      setSelectedPositionIds((loadedStep?.approvers || []).map((approver) => approver.positionId));
      writeSessionCache(cacheKey, { step: loadedStep });
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to load flow step details. Please try again.";
      setErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, approvalFlowId, cacheKey, stepId]);

  useEffect(() => {
    loadStep({ silent: true });
    loadLookups();
  }, [approvalFlowId, stepId, loadStep, loadLookups]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadStep({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadStep]);

  const togglePositionSelection = (positionId) => {
    setSelectedPositionIds((current) =>
      current.includes(positionId)
        ? current.filter((id) => id !== positionId)
        : [...current, positionId],
    );
  };

  const resetStepSelections = () => {
    setLevelId(step?.level?.id || "");
    setSelectedPositionIds((step?.approvers || []).map((approver) => approver.positionId));
    setDraggedPositionId("");
  };

  const getPositionSelectionOrder = (positionId) => {
    const order = selectedPositionIds.indexOf(positionId);
    return order >= 0 ? order + 1 : null;
  };

  const getPositionName = (positionId) => {
    const matched = positions.find((position) => position.id === positionId);
    return matched?.positionName || "Unknown position";
  };

  const handleSelectedDragStart = (positionId) => {
    setDraggedPositionId(positionId);
  };

  const handleSelectedDrop = (targetPositionId) => {
    if (!draggedPositionId || draggedPositionId === targetPositionId) {
      setDraggedPositionId("");
      return;
    }

    setSelectedPositionIds((current) => {
      const next = [...current];
      const fromIndex = next.indexOf(draggedPositionId);
      const toIndex = next.indexOf(targetPositionId);

      if (fromIndex < 0 || toIndex < 0) {
        return current;
      }

      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });

    setDraggedPositionId("");
  };

  const handleUpdateStep = async (ev) => {
    ev.preventDefault();

    if (!apiBaseUrl || !approvalFlowId || !stepId) {
      setFormErrorMessage("Unable to update this flow step right now.");
      return;
    }

    if (!levelId || selectedPositionIds.length === 0) {
      setFormErrorMessage("Please select a level and at least one approver position.");
      return;
    }

    try {
      setIsSaving(true);
      setFormErrorMessage("");

      await axios.patch(`${apiBaseUrl}/organizations/approval-flows/${approvalFlowId}/steps/${stepId}`, {
        levelId,
        positionIds: selectedPositionIds,
      });

      await loadStep();
      setOpenEditModal(false);
      setOpenSuccessModal(true);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to update flow step. Please try again.";
      setFormErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteStep = async () => {
    if (!apiBaseUrl || !approvalFlowId || !stepId) {
      setDeleteErrorMessage("Unable to delete this flow step right now.");
      return;
    }

    try {
      setIsDeleting(true);
      setDeleteErrorMessage("");

      await axios.delete(`${apiBaseUrl}/organizations/approval-flows/${approvalFlowId}/steps/${stepId}`);

      setOpenDeleteModal(false);
      setOpenDeleteSuccessModal(true);
      sessionStorage.removeItem("selectedApprovalFlowStepId");

      setTimeout(() => {
        navigate("/company-settings/leave-flow", {
          state: { approvalFlowId },
        });
      }, 1200);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to delete flow step. Please try again.";
      setDeleteErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const title = step?.approvalFlow?.approvalName || "Approval Flow Step";
  const description = step?.approvalFlow?.approvalDescription || "No approval flow description available.";
  const approverCount = step?.approvers?.length || 0;
  const createdAt = step?.createdAt
    ? new Date(step.createdAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "-";

  return (
    <div className="min-h-screen">
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Approval Flows", path: "/company-settings/approval-flow" },
          { label: step?.approvalFlow?.approvalName || "Flow Details", path: "/company-settings/leave-flow", },
          { label: step?.level?.levelName || "Flow Step" },
        ]}
        title={step?.level?.levelName || "Flow Step"}
        subtitle="Approval step details and approver selection."
        fallbackPath="/company-settings/leave-flow"
        backLabel="Back to Flow"
        actions={(
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setFormErrorMessage("");
                resetStepSelections();
                setOpenEditModal(true);
              }}
              className="border border-[#1234CD] text-[#1234CD] px-4 py-2 rounded-xl"
            >
              Edit
            </button>
            <button
              onClick={() => setOpenDeleteModal(true)}
              className="border border-red-500 text-red-600 px-4 py-2 rounded-xl"
            >
              Delete
            </button>
          </div>
        )}
      />

      {isLoading && !step ? (
        <FetchLoadingState message="Loading flow step details..." className="min-h-[60vh]" />
      ) : null}

      {errorMessage && !step ? (
        <div className="px-6 py-4 text-sm text-red-600" role="alert">
          {errorMessage}
        </div>
      ) : null}

      {!isLoading && step ? (
        <InfoCard
          details="Approval Flow Step"
          title={title}
          description={description}
          metaData={[
            { label: "Date created", value: createdAt },
            { label: "Level", value: step?.level?.levelName || "-" },
            {
              label: "Approvers",
              value: (
                <div className="flex items-center gap-2">
                  <span>{step?.approverNames || "-"}</span>
                  <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-[#E9EEFF] text-[#1234CD] text-xs font-semibold">
                    {approverCount}
                  </span>
                </div>
              ),
            },
          ]}
        />
      ) : null}

      <DeleteItem isOpen={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
        <div>
          <img className="w-40 mx-auto animate-pulse" src={DeleteImg} alt="delete flow step" />
          <h1 className="text-lg text-center text-[#000000] font-bold">Continue to delete this flow step?</h1>
          <p className="text-sm text-center mt-2">Action cannot be undone.</p>
          {deleteErrorMessage && (
            <p className="text-sm text-center mt-3 text-red-600" role="alert">
              {deleteErrorMessage}
            </p>
          )}
        </div>
        <div className="flex items-center py-4 gap-4 my-4">
          <button
            onClick={() => setOpenDeleteModal(false)}
            className="border-[#1233CC] py-1 border-2 text-[#1233CC] font-medium px-4 rounded-[10px] w-[102px]"
          >
            No
          </button>
          <button
            className="text-[16px] font-medium px-4 py-1 bg-[#1233CC] rounded-[10px] w-[102px] text-white disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={handleDeleteStep}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Yes"}
          </button>
        </div>
      </DeleteItem>

      <SuccessfulDelete isOpen={openDeleteSuccessModal} onClose={() => setOpenDeleteSuccessModal(false)}>
        <div>
          <img src={verifyImg} alt="flow step deleted" className="w-20 animate-ping mx-auto" />
          <div className="text-center mt-6">
            <h2 className="text-black text-[20px] leading-[27px] font-bold mb-2">Flow step deleted</h2>
            <p className="text-[#74777B] text-[14px] leading-[18.9px]">The flow step was removed successfully.</p>
          </div>
        </div>
      </SuccessfulDelete>

      <InviteModal isOpen={openEditModal} onClose={() => setOpenEditModal(false)}>
        <div className="my-8 font-Outfit">
          <p className="text-2xl font-semibold text-black">Edit Flow Step</p>
          <form className="my-8" onSubmit={handleUpdateStep}>
            {formErrorMessage && (
              <p className="mb-4 text-sm text-red-600" role="alert">
                {formErrorMessage}
              </p>
            )}

            <div>
              <label className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px]">Level</label>
              <select
                value={levelId}
                onChange={(ev) => setLevelId(ev.target.value)}
                className="w-full p-3 mb-4 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5]"
              >
                <option value="">Select level</option>
                {levels.map((level) => (
                  <option key={level.id} value={level.id}>
                    {level.levelName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px]">Approvers (Positions)</label>
              <div className="max-h-44 overflow-y-auto border-2 border-[#F3F5F5] rounded-xl p-3">
                {positions.map((position) => (
                  <label key={position.id} className="flex items-center justify-between gap-2 py-1 text-sm text-black">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedPositionIds.includes(position.id)}
                        onChange={() => togglePositionSelection(position.id)}
                      />
                      <span>{position.positionName}</span>
                    </div>
                    {getPositionSelectionOrder(position.id) ? (
                      <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1 rounded-full bg-[#E9EEFF] text-[#1234CD] text-xs font-semibold">
                        {getPositionSelectionOrder(position.id)}
                      </span>
                    ) : null}
                  </label>
                ))}
              </div>
              {selectedPositionIds.length > 0 ? (
                <p className="mt-2 text-xs text-[#5F6B8A]">
                  Selection order is shown with badges and will be saved on update.
                </p>
              ) : null}

              {selectedPositionIds.length > 1 ? (
                <div className="mt-3 rounded-xl border border-[#E9EEFF] p-3">
                  <p className="mb-2 text-xs font-medium text-[#5F6B8A]">Drag to reorder selected approvers</p>
                  <div className="space-y-2">
                    {selectedPositionIds.map((positionId, index) => (
                      <div
                        key={positionId}
                        draggable
                        onDragStart={() => handleSelectedDragStart(positionId)}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={() => handleSelectedDrop(positionId)}
                        onDragEnd={() => setDraggedPositionId("")}
                        className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm cursor-move ${
                          draggedPositionId === positionId ? "border-[#1234CD] bg-[#F3F6FF]" : "border-[#EEF1FB] bg-white"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1 rounded-full bg-[#E9EEFF] text-[#1234CD] text-xs font-semibold">
                            {index + 1}
                          </span>
                          <span>{getPositionName(positionId)}</span>
                        </div>
                        <span className="text-xs text-gray-400">Drag</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="flex justify-center gap-12 mt-10 items-end">
              <SimpleButton
                onClick={() => setOpenEditModal(false)}
                type="button"
                className="font-[700] leading-[21.6px] flex justify-center items-center border w-40 rounded-xl py-[16px] border-[#1234CD] text-[#1234CD] text-[16px]"
              >
                Cancel
              </SimpleButton>

              <SimpleButton
                type="submit"
                className="font-[700] leading-[21.6px] flex justify-center items-center w-40 rounded-xl text-white py-[16px] bg-[#1234CD] text-[16px] disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={isSaving}
              >
                <span>{isSaving ? "Updating..." : "Update"}</span>
              </SimpleButton>
            </div>
          </form>
        </div>
      </InviteModal>

      <SuccessModal isOpen={openSuccessModal} onClose={() => setOpenSuccessModal(false)} />
    </div>
  );
};

export default LeaveFlowDetails;
